import { useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { createMemoCell } from '@/lib/create-memo-cell'
import {
  RelationshipEditor,
  useRelationshipEditor,
  type RelationshipItem,
} from './relationship-editor'
import { useCountriesSearch, useStatesSearch } from '@/hooks/use-reference-data'
import { useUpdateCompany } from '@/hooks/use-companies'
import { showError } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { useCanEditCompany } from '@/hooks/use-permissions'

type LocationFieldType = 'location.country.name' | 'location.state.name'

interface LocationFieldCellProps {
  companyId: string
  primaryOwnerId?: string | null
  secondaryOwnerId?: string | null
  fieldType: LocationFieldType
  /** Current country (needed for state filtering and optimistic updates) */
  currentCountry?: { id: string; name: string } | null
  /** Current state */
  currentState?: { id: string; name: string } | null
  /** Current city (for preserving in optimistic updates) */
  currentCity?: { id: string; name: string } | null
}

function LocationFieldCellBase({
  companyId,
  primaryOwnerId,
  secondaryOwnerId,
  fieldType,
  currentCountry,
  currentState,
  currentCity,
}: LocationFieldCellProps) {
  const canEditCompany = useCanEditCompany()
  const canEdit = canEditCompany(primaryOwnerId, secondaryOwnerId)
  const { open, setOpen, searchTerm, setSearchTerm, debouncedSearch } =
    useRelationshipEditor()

  const updateCompany = useUpdateCompany()

  const isCountryField = fieldType === 'location.country.name'
  const isStateField = fieldType === 'location.state.name'
  const canEditState = isStateField && !!currentCountry
  const currentItem = isCountryField ? currentCountry : currentState

  // Local optimistic value for instant UI feedback without table-wide optimistic cache updates.
  const [optimisticItem, setOptimisticItem] = useState<
    RelationshipItem | null | undefined
  >(undefined)
  const [prevItemId, setPrevItemId] = useState(currentItem?.id)

  if (currentItem?.id !== prevItemId) {
    setPrevItemId(currentItem?.id)
    setOptimisticItem(undefined)
  }

  const mutationIdRef = useRef(0)

  // Get the appropriate search hook based on field type
  const countriesSearch = useCountriesSearch({
    search: debouncedSearch,
    enabled: open && isCountryField,
  })

  const statesSearch = useStatesSearch({
    countryId: currentCountry?.id ?? '',
    search: debouncedSearch,
    enabled: open && canEditState,
  })

  // Determine which search results and loading state to use
  const { searchData, isSearchLoading, placeholder } = useMemo(() => {
    if (isCountryField) {
      return {
        searchData: countriesSearch.data?.data?.map((c) => ({
          id: c.id,
          name: c.name,
        })),
        isSearchLoading: countriesSearch.isLoading,
        placeholder: 'Search countries...',
      }
    }
    if (canEditState) {
      return {
        searchData: statesSearch.data?.data?.map((s) => ({
          id: s.id,
          name: s.name,
        })),
        isSearchLoading: statesSearch.isLoading,
        placeholder: 'Search states...',
      }
    }
    return {
      searchData: undefined as RelationshipItem[] | undefined,
      isSearchLoading: false,
      placeholder: 'Search...',
    }
  }, [isCountryField, canEditState, countriesSearch, statesSearch])

  const handleAdd = (item: RelationshipItem) => {
    const mutId = ++mutationIdRef.current
    setOptimisticItem(item)

    if (isCountryField) {
      // When changing country, clear state (new country may not have same states)
      updateCompany.mutate(
        {
          id: companyId,
          data: { countryId: item.id, stateId: undefined },
          optimistic: false,
          // Provides location payload if optimistic cache patching is enabled.
          location: {
            country: { id: item.id, name: item.name },
            state: null,
            city: null,
          },
        },
        {
          onError: (error) => {
            if (mutationIdRef.current === mutId) {
              setOptimisticItem(undefined)
            }
            showError(error, 'Failed to update country')
          },
        }
      )
    } else {
      // When changing state, keep the current country
      if (!currentCountry) {
        return
      }
      updateCompany.mutate(
        {
          id: companyId,
          data: { countryId: currentCountry.id, stateId: item.id },
          optimistic: false,
          // Provides location payload if optimistic cache patching is enabled.
          location: {
            country: currentCountry,
            state: { id: item.id, name: item.name },
            city: currentCity,
          },
        },
        {
          onError: (error) => {
            if (mutationIdRef.current === mutId) {
              setOptimisticItem(undefined)
            }
            showError(error, 'Failed to update state')
          },
        }
      )
    }
  }

  const handleRemove = () => {
    const mutId = ++mutationIdRef.current
    setOptimisticItem(null)

    if (isCountryField) {
      // Removing country clears the entire location
      updateCompany.mutate(
        {
          id: companyId,
          data: { locationId: undefined },
          optimistic: false,
          location: undefined,
        },
        {
          onError: (error) => {
            if (mutationIdRef.current === mutId) {
              setOptimisticItem(undefined)
            }
            showError(error, 'Failed to remove country')
          },
        }
      )
    } else {
      // Removing state keeps the country but clears the state
      if (!currentCountry) return
      updateCompany.mutate(
        {
          id: companyId,
          data: { countryId: currentCountry.id, stateId: undefined },
          optimistic: false,
          // Provides location payload if optimistic cache patching is enabled.
          location: {
            country: currentCountry,
            state: null,
            city: currentCity,
          },
        },
        {
          onError: (error) => {
            if (mutationIdRef.current === mutId) {
              setOptimisticItem(undefined)
            }
            showError(error, 'Failed to remove state')
          },
        }
      )
    }
  }

  // Build current values array for the editor
  const displayItem =
    optimisticItem !== undefined ? optimisticItem : currentItem
  const currentValues: RelationshipItem[] = displayItem
    ? [{ id: displayItem.id, name: displayItem.name }]
    : []

  const isEmpty = !displayItem
  const isDisabled = isStateField && !currentCountry

  if (!canEdit) {
    return (
      <span
        className={cn(
          'text-xs',
          isEmpty ? 'text-muted-foreground' : 'truncate'
        )}
      >
        {displayItem?.name ?? '-'}
      </span>
    )
  }

  // If state field is disabled (no country), show non-interactive cell
  if (isDisabled) {
    return (
      <span className="text-xs text-muted-foreground/50 cursor-not-allowed">
        -
      </span>
    )
  }

  return (
    <RelationshipEditor
      currentValues={currentValues}
      isMulti={false}
      onAdd={handleAdd}
      onRemove={handleRemove}
      searchData={searchData}
      isSearchLoading={isSearchLoading}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      placeholder={placeholder}
      open={open}
      onOpenChange={setOpen}
    >
      <Button
        variant="ghost"
        className={cn(
          'flex items-center justify-between gap-1 h-auto px-1 -mx-1 w-full',
          'hover:bg-muted/50'
        )}
      >
        {isEmpty ? (
          <span className="text-xs text-muted-foreground">-</span>
        ) : (
          <span className="text-xs truncate">{displayItem?.name}</span>
        )}
        <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
      </Button>
    </RelationshipEditor>
  )
}

export const LocationFieldCell = createMemoCell(
  LocationFieldCellBase,
  'LocationFieldCell'
)
